import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  CheckCircle,
  Globe,
  Award,
  Mail,
  Star,
  Target,
  Leaf,
  Shield,
  FileCheck,
  Building2,
  TrendingUp,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const AMFORI_BSCI_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#3d1f47] via-[#5a2d64] to-[#6d3a7f] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-violet-400 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Users size={18} className="text-violet-300" />
            <span>Social Compliance Initiative</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-violet-300 via-purple-300 to-pink-300">
            AMFORI BSCI
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
            Improving Labor Rights and Working Conditions Through Global Supply
            Chains
          </p>
        </motion.div>
      </section>

      {/* What is BSCI */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is AMFORI BSCI Certification?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              AMFORI BSCI (Business Social Compliance Initiative) is an
              international business-driven platform that aims to improve
              working conditions and labor rights in global supply chains. It
              provides a comprehensive framework for assessing and ensuring
              compliance with social responsibility standards.
            </p>
            <p>
              The certification demonstrates that an organization has
              implemented a social compliance management system adhering to
              international labor standards and ethical business practices, with
              focus on fair labor, employee welfare, health and safety, and
              sustainable sourcing.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of AMFORI BSCI Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Ethical Sourcing and Fair Trade",
                desc: "Ensures ethical labor practices including fair wages and freedom of association",
                icon: Target,
              },
              {
                title: "Employee Health and Safety",
                desc: "Verifies safe working environment with proper safety protocols",
                icon: Shield,
              },
              {
                title: "Working Hours and Conditions",
                desc: "Ensures compliance with labor laws for fair working arrangements",
                icon: TrendingUp,
              },
              {
                title: "Non-Discrimination",
                desc: "Promotes diversity and equal opportunities in the workplace",
                icon: Users,
              },
              {
                title: "Supply Chain Transparency",
                desc: "Provides visibility to track and monitor supplier working conditions",
                icon: Globe,
              },
              {
                title: "Worker Rights Protection",
                desc: "Ensures no child labor, forced labor, or discrimination",
                icon: Award,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-violet-600 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* About AMFORI BSCI */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is AMFORI BSCI?
          </h2>
          <div className="bg-gradient-to-r from-violet-50 to-purple-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              AMFORI BSCI provides a platform and tools for businesses to:
            </p>
            <ul className="space-y-3">
              {[
                "Assess suppliers and ensure compliance with social responsibility standards",
                "Audit supply chain practices to identify gaps in ethical labor practices",
                "Develop action plans to improve labor conditions in the supply chain",
                "Engage suppliers in the improvement process",
                "Monitor systems ensuring companies maintain social compliance over time",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Why AMFORI BSCI Matters
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Improve Worker Welfare and protect labor rights",
              "Access Global Markets with BSCI requirement compliance",
              "Risk Mitigation reducing supply chain risks",
              "Build Consumer Trust through ethical sourcing",
              "Enhance Supply Chain Relationships with partners",
              "Demonstrate CSR Commitment to stakeholders",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-violet-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Benefits of AMFORI BSCI Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Enhanced Supply Chain Transparency across operations",
              "Regulatory Compliance with international labor laws",
              "Consumer Trust and brand reputation building",
              "Increased Market Access for buyers with requirements",
              "Risk Reduction from labor violations and disputes",
              "Worker Welfare improvements in facilities",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-violet-100 to-purple-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-violet-700 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of AMFORI BSCI Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Supply Chain Responsibility and ethical practices",
              "Access to Retailers and global market opportunities",
              "Worker Welfare and human rights commitment",
              "Environmental Sustainability initiatives",
              "Brand Reputation and consumer loyalty",
              "Legal Compliance with labor standards",
              "Supply Relationship Improvement with partners",
              "Competitive Advantage in ethical markets",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries Using AMFORI BSCI
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Fashion and Textiles",
              "Manufacturing and Processing",
              "Retail and Distribution",
              "Electronics and Technology",
              "Food and Beverage",
              "Logistics and Supply Chain",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg"
              >
                <Building2 className="w-5 h-5 text-violet-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Achieve AMFORI BSCI Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Join AMFORI BSCI Network",
                desc: "Register as member to access platform tools and resources",
              },
              {
                step: 2,
                title: "Assess Supply Chain",
                desc: "Evaluate suppliers for compliance with Code of Conduct",
              },
              {
                step: 3,
                title: "Complete Self-Assessment",
                desc: "Conduct self-assessment based on BSCI Code of Conduct",
              },
              {
                step: 4,
                title: "Supplier Audits",
                desc: "Perform audits to identify improvement areas",
              },
              {
                step: 5,
                title: "Develop Action Plans",
                desc: "Create plans to address compliance gaps",
              },
              {
                step: 6,
                title: "Certification Audit",
                desc: "Undergo external audit to verify compliance",
              },
              {
                step: 7,
                title: "Ongoing Monitoring",
                desc: "Continuously monitor and improve practices",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-violet-50 via-purple-50 to-pink-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for AMFORI BSCI?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions provides expert AMFORI BSCI consultancy services,
              guiding businesses through social compliance certification to
              improve working conditions and supply chain transparency.
            </p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "BSCI Membership Registration",
                "Self-Assessment and Gap Analysis",
                "Supplier Audits and Compliance",
                "Action Plan Development",
                "Employee Training Services",
                "Monitoring and Reporting Support",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-violet-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help businesses achieve AMFORI BSCI certification ensuring
              ethical labor practices and socially responsible supply chain
              management.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-violet-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-violet-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default AMFORI_BSCI_Details;
